/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListAudiencesRequest,
  ListAudiencesSecurity,
} from "../models/operations/listaudiences.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  AudiencesQueryData,
  buildAudiencesQuery,
  prefetchAudiences,
  queryKeyAudiences,
} from "./audiences.core.js";
export {
  type AudiencesQueryData,
  buildAudiencesQuery,
  prefetchAudiences,
  queryKeyAudiences,
};

export type AudiencesQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listAudiences plugins
 *
 * @remarks
 * List the audiences that can be assigned to plugins.
 */
export function useAudiences(
  request?: ListAudiencesRequest | undefined,
  security?: ListAudiencesSecurity | undefined,
  options?: QueryHookOptions<AudiencesQueryData, AudiencesQueryError>,
): UseQueryResult<AudiencesQueryData, AudiencesQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildAudiencesQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listAudiences plugins
 *
 * @remarks
 * List the audiences that can be assigned to plugins.
 */
export function useAudiencesSuspense(
  request?: ListAudiencesRequest | undefined,
  security?: ListAudiencesSecurity | undefined,
  options?: SuspenseQueryHookOptions<AudiencesQueryData, AudiencesQueryError>,
): UseSuspenseQueryResult<AudiencesQueryData, AudiencesQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildAudiencesQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setAudiencesData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: AudiencesQueryData,
): AudiencesQueryData | undefined {
  const key = queryKeyAudiences(...queryKeyBase);

  return client.setQueryData<AudiencesQueryData>(key, data);
}

export function invalidateAudiences(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "plugins", "listAudiences", ...queryKeyBase],
  });
}

export function invalidateAllAudiences(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "plugins", "listAudiences"],
  });
}
